import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { PaginatedDocs } from '@nhanho/shared'
import type { ListParams } from './api'

/**
 * State của một trang danh sách, **lưu trong URL** (`?page=2&search=cao&category=…`):
 * reload không mất filter, back/forward hoạt động, và copy link chia sẻ được đúng view.
 *
 * Không dùng query lib: `fetch` + `useState` đủ, và toàn bộ state nhìn thấy được —
 * bài học từ con bug form-state của Payload.
 *
 * ⚠️ `fetcher` và `defaults` được giữ trong `ref`, **không** nằm trong deps. Caller luôn
 * truyền arrow function / object literal mới mỗi render; nếu để chúng vào deps thì
 * `params` đổi identity mỗi render → effect chạy lại → `setData` → render → … fetch vô hạn.
 * Đây từng là bug thật (2026-09-08): mở `/products` là API nhận request liên tục.
 */
export function useList<T>(
  fetcher: (params: ListParams) => Promise<PaginatedDocs<T>>,
  defaults: ListParams = {},
) {
  const [sp, setSp] = useSearchParams()
  const [data, setData] = useState<PaginatedDocs<T> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  /** Chỉ đọc lần mount — `defaults` là mặc định, không phải thứ đổi theo render. */
  const defaultsRef = useRef(defaults)

  // `sp` chỉ đổi identity khi `location.search` đổi (react-router memo theo chuỗi search).
  const params = useMemo<ListParams>(() => {
    const out: ListParams = { ...defaultsRef.current }
    sp.forEach((v, k) => {
      out[k] = k === 'page' || k === 'limit' ? Number(v) : v
    })
    return out
  }, [sp])

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetcherRef
      .current(params)
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [params, tick])

  const set = useCallback(
    (patch: Record<string, string | number | null | undefined>) => {
      const next = new URLSearchParams(sp)
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined || v === '') next.delete(k)
        else next.set(k, String(v))
      }
      // Đổi filter/tìm kiếm thì về trang 1 — trừ khi chính `page` đang được đổi.
      if (!('page' in patch)) next.delete('page')
      setSp(next, { replace: true })
    },
    [sp, setSp],
  )

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  return { data, loading, error, params, set, refresh }
}
